import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Icon from '../components/common/Icon';
import CtaSection from '../components/common/CtaSection';
import Breadcrumbs from '../components/common/Breadcrumbs';

const OrganizzazionePage = () => {
  const breadcrumbs = [{ label: 'Home', path: '/' }, { label: 'Chi Siamo', path: '/chi-siamo' }, { label: 'Organizzazione' }];
  
  const organs = [
    { icon: 'Landmark', title: 'Assemblea Nazionale', text: "Il massimo organo deliberante. Si riunisce almeno una volta l'anno con delegati da tutte le regioni.", color: 'var(--color-primary)' },
    { icon: 'Settings', title: 'Direzione Nazionale', text: "L'organo esecutivo che attua le decisioni dell'Assemblea e coordina l'azione politica.", color: 'var(--color-secondary)' },
    { icon: 'Users', title: 'Segreteria Nazionale', text: 'Il team operativo guidato dal Segretario Nazionale.', link: '/segreteria', color: 'var(--color-accent)' },
    { icon: 'LayoutGrid', title: 'Coordinamento Regionale', text: 'I segretari regionali coordinano il lavoro nelle 20 regioni.', color: 'var(--color-primary-dark)' },
  ];

  const territory = [
    { count: '20', label: 'Federazioni Regionali', text: 'Coordinamenti regionali che indirizzano la linea politica sul territorio.', link: '/federazioni-regionali', linkLabel: 'Vedi regionali →', bg: 'var(--color-primary)', image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=800&auto=format&fit=crop' },
    { count: '107', label: 'Federazioni Provinciali', text: 'Federazioni territoriali che coordinano i circoli e le attività locali.', link: '/federazioni-provinciali', linkLabel: 'Esplora →', bg: 'var(--color-secondary)', image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800&auto=format&fit=crop' },
    { count: '850', label: 'Circoli', text: 'Circoli attivi in tutta Italia, dal centro alle periferie.', link: '/circoli', linkLabel: 'Trova il tuo →', bg: 'var(--color-primary-dark)', image: 'https://images.unsplash.com/photo-1529156069898-49953eb1b5ae?q=80&w=800&auto=format&fit=crop' },
  ];


  return (
    <Layout>
      {/* EPIC HERO */}
      <section className="hero--epic">
        <div className="hero__bg">
          <div className="hero__image" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1600&auto=format&fit=crop)' }}></div>
          <div className="hero__overlay" style={{ background: 'linear-gradient(to top, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.2) 100%)' }}></div>
        </div>
        
        <div className="hero__decor">
          <div className="hero__square animate-float" style={{ '--x': '80%', '--y': '20%', '--s': '140px', '--d': '14s', opacity: 0.3 }}></div>
          <div className="hero__square animate-float-slow" style={{ '--x': '10%', '--y': '30%', '--s': '100px', '--d': '10s', opacity: 0.2 }}></div>
        </div>

        <div className="hero__content-epic container">
          <div className="reveal" style={{ marginBottom: 'var(--space-8)' }}>
            <Breadcrumbs items={breadcrumbs} style={{ color: 'inherit', padding: 0 }} />
          </div>
          
          <h1 className="hero__title-epic text-reveal-trigger">
            <span className="line text-reveal"><span className="word text-reveal__inner" style={{ transitionDelay: '0.1s' }}>Come siamo</span></span>
            <span className="line text-reveal"><span className="word accent text-reveal__inner" style={{ transitionDelay: '0.3s' }}>Organizzati.</span></span>
          </h1>
          
          <p className="hero__subtitle-epic reveal stagger-3" style={{ opacity: 1 }}>
            Una struttura democratica, partecipativa e capillare. Dai grandi organi decisionali nazionali al lavoro quotidiano nei piccoli circoli.
          </p>
        </div>
      </section>

      {/* ORGANI NAZIONALI */}
      <section className="section" style={{ background: 'var(--color-bg)' }}>
        <div className="container">
          <div className="reveal">
            <h2 className="h1 mb-4 text-center">Organi Nazionali</h2>
            <p className="text-body-sm text-secondary text-center mb-12 max-w-2xl mx-auto">La democrazia è alla base di ogni nostra decisione. Scopri chi sono i garanti del nostro progetto politico.</p>
          </div>

          <div className="grid grid-2 gap-6 mt-8">
            {organs.map((o, idx) => {
              const Tag = o.link ? Link : 'div';
              const tagProps = o.link ? { to: o.link } : {};
              return (
                <Tag 
                  {...tagProps} 
                  className={`card-premium reveal reveal--scale stagger-${(idx % 2) + 1}`} 
                  style={{ 
                    padding: 'var(--space-8)', 
                    background: '#fff', 
                    borderRadius: 'var(--radius-xl)',
                    display: 'block',
                    border: '1px solid var(--color-border-subtle)',
                    textDecoration: 'none',
                    color: 'inherit'
                  }} 
                  key={o.title}
                >
                  <div style={{ 
                    width: '56px', 
                    height: '56px', 
                    borderRadius: 'var(--radius-lg)', 
                    background: `${o.color}15`, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: o.color,
                    marginBottom: 'var(--space-6)'
                  }}>
                    <Icon name={o.icon} size={28} />
                  </div>
                  <h3 className="h4 mb-3">{o.title}</h3>
                  <p className="text-body-sm text-secondary mb-4">{o.text}</p>
                  {o.link && (
                    <span className="text-small font-bold text-primary flex items-center gap-2">
                      Vedi dettagli <Icon name="ArrowRight" size={16} />
                    </span>
                  )}
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* STRUTTURA TERRITORIALE - BENTO STYLE */}
      <section className="section bg-subtle" style={{ overflow: 'hidden' }}>
        <div className="container">
          <div className="reveal">
            <h2 className="h1 mb-12">La forza del territorio</h2>
          </div>

          <div className="grid grid-3 gap-8">
            {territory.map((t, idx) => (
              <div 
                key={t.label} 
                className={`reveal reveal--scale stagger-${idx + 1}`}
                style={{ 
                  background: '#fff', 
                  borderRadius: 'var(--radius-2xl)', 
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-lg)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                  <img src={t.image} alt={t.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ 
                    position: 'absolute', 
                    top: '20px', 
                    right: '20px', 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '50%', 
                    background: t.bg, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#fff', 
                    fontSize: '1.25rem', 
                    fontWeight: 800,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                  }}>
                    {t.count}
                  </div>
                </div>
                <div style={{ padding: 'var(--space-8)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 className="h3 mb-3">{t.label}</h3>
                  <p className="text-body-sm text-secondary mb-6 flex-1">{t.text}</p>
                  <Link 
                    to={t.link} 
                    className="btn btn--lg" 
                    style={{ 
                      width: '100%', 
                      background: `${t.bg}10`, 
                      color: t.bg,
                      border: `1px solid ${t.bg}30`,
                      fontWeight: 700
                    }}
                  >
                    {t.linkLabel}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section reveal reveal--scale">
        <div className="container">
          <CtaSection 
            title="Sali a bordo" 
            subtitle="C'è un posto per te in ogni circolo d'Italia. Unisciti alla comunità dei Giovani Democratici."
          >
            <div className="btn-group reveal stagger-2" style={{ justifyContent: 'center' }}>
              <Link to="/partecipa" className="btn btn--primary btn--lg btn--glow">Iscriviti ora</Link>
              <Link to="/contatti" className="btn btn--outline btn--lg">Chiedi informazioni</Link>
            </div>
          </CtaSection>
        </div>
      </section>
    </Layout>
  );
};

export default OrganizzazionePage;
